import React, { useEffect, useRef, useState } from "react";

import {
  FarmFootprintingCapabilities,
  formatFractionInput,
  formatPercentageInput,
  getFarmFootprintingCapabilities,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isValidPercentageDecimal,
  queryIsLoading,
  useGetCarbonProgramByFarmId,
  useGetFootprintingFarmResourceConsumption,
  useMutateFootprintingFarmResourceConsumption,
  useTranslation,
} from "utils";

import { FootprintingFarmResourceConsumption } from "models";

import { AlertBanner, Card, Flex, Form, FormInstance, InputNumber, PageSpin, Typography } from "components";

type FarmElectricityFormProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
  setIsSaveComplete?: (isSaveComplete: boolean) => void;
};

// Each bucket is a separate, non-overlapping share of the farm's electricity. Shared electricity (capability null) is always
// asked; livestock buckets only when the carbon program covers that livestock
export const ELECTRICITY_BUCKETS = [
  { field: "electricityKwh", capability: null, copyKey: "shared", dataTestId: "footprinting-farm-electricity-input" },
  { field: "dairyElectricityKwh", capability: "dairyEnabled", copyKey: "dairyTotal", dataTestId: "footprinting-farm-dairy-kwh-input" },
  { field: "beefElectricityKwh", capability: "beefEnabled", copyKey: "beefTotal", dataTestId: "footprinting-farm-beef-kwh-input" },
  { field: "porkElectricityKwh", capability: "porkEnabled", copyKey: "porkTotal", dataTestId: "footprinting-farm-pork-kwh-input" },
] as const satisfies ReadonlyArray<{
  field: keyof FootprintingFarmResourceConsumption;
  capability: keyof FarmFootprintingCapabilities | null;
  copyKey: string;
  dataTestId: string;
}>;

type ElectricityBucket = (typeof ELECTRICITY_BUCKETS)[number];
type ElectricityBucketField = ElectricityBucket["field"];

export const isElectricityBucketCovered = ({ capability }: ElectricityBucket, capabilities: FarmFootprintingCapabilities) =>
  capability === null || capabilities[capability];
type ElectricityValues = Record<ElectricityBucketField, number | null>;

const toElectricityValues = (getValue: (bucket: ElectricityBucket) => number | null): ElectricityValues =>
  Object.fromEntries(ELECTRICITY_BUCKETS.map((bucket) => [bucket.field, getValue(bucket)])) as ElectricityValues;

const FarmElectricityForm = ({ primaryDataId, farmId, year, readOnly, setIsSaveComplete }: FarmElectricityFormProps) => {
  const { t } = useTranslation("footprintingFarm");
  const formRef = useRef<FormInstance>(null);
  const carbonProgram = useGetCarbonProgramByFarmId(farmId);
  const capabilities = getFarmFootprintingCapabilities(carbonProgram.data);
  const enabledBuckets = ELECTRICITY_BUCKETS.filter((bucket) => isElectricityBucketCovered(bucket, capabilities));

  const farmResourceConsumptionQuery = useGetFootprintingFarmResourceConsumption(primaryDataId ?? "");

  const [electricityValues, setElectricityValues] = useState<ElectricityValues>(() => toElectricityValues(() => null));
  const [renewablesPct, setRenewablesPct] = useState<number | null>(null);

  const { mutate: mutateFarmResourceConsumption } = useMutateFootprintingFarmResourceConsumption(
    farmId,
    year,
    {
      id: farmResourceConsumptionQuery.data?.id,
      footprintingFarmPrimaryDataId: primaryDataId ?? "",
      // Buckets the program doesn't cover are cleared; if the program is unknown, stored values are kept rather than wiped
      ...toElectricityValues((bucket) => (carbonProgram.data && !isElectricityBucketCovered(bucket, capabilities) ? null : electricityValues[bucket.field])),
      renewablesPct,
    },
    primaryDataId
  );

  const isLoading = [farmResourceConsumptionQuery, carbonProgram].some(queryIsLoading);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        setIsSaveComplete?.(false);
        mutateFarmResourceConsumption(undefined, {
          onSuccess: () => {
            setIsSaveComplete?.(true);
          },
          onError: () => {
            setIsSaveComplete?.(true);
          },
        });
      }
    },
    [isLoading, mutateFarmResourceConsumption, readOnly, setIsSaveComplete]
  );

  // Note - this will only run once because the query above is only the GET
  useEffect(() => {
    if (farmResourceConsumptionQuery.data) {
      const storedValues = toElectricityValues(({ field }) => farmResourceConsumptionQuery.data[field] ?? null);
      setElectricityValues(storedValues);
      setRenewablesPct(farmResourceConsumptionQuery.data.renewablesPct ?? null);

      formRef.current?.setFieldsValue({
        ...storedValues,
        renewablesPct: formatFractionInput(farmResourceConsumptionQuery.data.renewablesPct ?? null),
      });

      // this stops the inputs from validating on first form load, but will validate if user
      // nagivates forward and then back with an input that was left as null
      const fieldsToValidate = [...ELECTRICITY_BUCKETS.map(({ field }) => field), "renewablesPct" as const].some(
        (field) => farmResourceConsumptionQuery.data[field] !== null
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [farmResourceConsumptionQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-farm-electricity-form" className="padding-200">
      <Form ref={formRef}>
        <Flex vertical gap={16}>
          {enabledBuckets.length > 1 && (
            <AlertBanner type="info" dataTestId="footprinting-farm-electricity-buckets-banner" description={t("electricityForm.separateBucketsBanner")} />
          )}
          {enabledBuckets.map(({ field, copyKey, dataTestId }) => (
            <InputNumber
              key={field}
              className="floating-label"
              dataTestId={dataTestId}
              disabled={readOnly}
              name={field}
              label={t(`electricityForm.question.${copyKey}`)}
              tooltip={t(`electricityForm.tooltip.${copyKey}`)}
              suffix="kWh"
              value={electricityValues[field]}
              initialValue={electricityValues[field]}
              onChange={(value) => setElectricityValues((previous) => ({ ...previous, [field]: value }))}
              precision={2}
              validateTrigger={["onBlur", "onInput"]}
              help={
                <Typography variant="paragraph-s" color="grey-600" className="margin-top-25">
                  {t(`electricityForm.help.${copyKey}`)}
                </Typography>
              }
              rules={[
                { required: true, message: " " }, // message is an empty to stop duplication error message showing up with the input name
                { type: "number", message: " " },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                      return Promise.reject(new Error(t(`electricityForm.error.${copyKey}`)));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          ))}
          <InputNumber
            className="floating-label"
            dataTestId="footprinting-farm-renewables-input"
            disabled={readOnly}
            name="renewablesPct"
            label={t("electricityForm.question.renewables")}
            tooltip={t("electricityForm.tooltip.renewables")}
            suffix="%"
            value={formatFractionInput(renewablesPct)}
            initialValue={formatFractionInput(renewablesPct)}
            onChange={(v) => setRenewablesPct(formatPercentageInput(v))}
            help={
              <Typography variant="paragraph-s" color="grey-600" className="margin-top-25">
                {t("electricityForm.help.renewables")}
              </Typography>
            }
            precision={2}
            validateTrigger={["onBlur", "onInput"]}
            rules={[
              { required: true, message: " " }, // message is an empty to stop duplication error message showing up with the input name
              { type: "number", message: " " },
              {
                validator: (_: unknown, v: number) => {
                  if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                    return Promise.reject(new Error(t("electricityForm.error.renewables")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          />
        </Flex>
      </Form>
    </Card>
  );
};

FarmElectricityForm.Key = "farm.electricity";
export { FarmElectricityForm };
